<!-- Shared layout wrapper for form fields providing label, focus/error styling, and error messages. -->

<script lang="ts" setup>
import type { FormFieldController } from '@nzyme/vue-forms/components/defineFormField.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { classProp } from '@nzyme/vue-utils/classProp.js';
import { defineProp } from '@nzyme/vue-utils/defineProp.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import { computed, useId } from 'vue';
import type { PropType } from 'vue';

import FieldInfo from './FieldInfo.vue';
import * as l from './FormFieldWrapper.loc.js';
import css from './FormFieldWrapper.vue?vue&type=style&index=0&lang.module.scss';

const props = defineProps({
  label: String,
  field: defineProp<FormFieldController<unknown>>({ required: true }),
  readonly: Boolean,
  disabled: Boolean,
  // Required can be bool or undefined
  required: defineProp<boolean>(),
  innerClass: classProp,
  errors: Array as PropType<string[]>,
});

const t = useTranslate();
const id = useId();
const inputId = computed(() => `${id}-input`);

defineSlots<
  Slots<{
    addon: { inputId: string };
    default: { inputId: string };
    header: { inputId: string };
    info: { inputId: string };
  }>
>();

const wrapperClass = computed(() => {
  const field = props.field;

  const isValue = field.value != null && field.value !== '';
  const isFocused = field.focused;
  const isError = field.errors.length > 0;

  return [
    css.field,
    isValue && css.isValue,
    isFocused && css.isFocused,
    isError && !props.disabled && !props.readonly && css.isError,
    props.readonly && css.isReadonly,
    props.disabled && css.isDisabled,
    props.label && css.hasLabel,
  ];
});

const label = computed(() => props.label ?? null);

const errorsToShow = computed(() => {
  let errors = props.field.errors;

  if (props.errors) {
    errors = errors.concat(props.errors);
  }

  return errors.filter(Boolean);
});
</script>

<template>
  <div
    :id="id"
    :class="wrapperClass"
  >
    <div
      v-if="label || $slots.header"
      :class="css.header"
    >
      <label
        v-if="label"
        :class="css.label"
        :for="inputId"
      >
        {{ label
        }}<span
          v-if="required"
          :class="css.required"
          >&nbsp;*</span
        >
        <span
          v-else-if="required === false"
          :class="css.optional"
          >&nbsp;({{ t(l.optional) }})</span
        >
      </label>

      <slot
        name="header"
        :input-id="inputId"
      />
    </div>

    <div :class="innerClass || css.input">
      <slot :input-id="inputId" />

      <div
        v-if="$slots.addon"
        :class="css.addon"
      >
        <slot
          name="addon"
          :input-id="inputId"
        />
      </div>
    </div>

    <FieldInfo
      v-for="(error, index) in errorsToShow"
      :key="index"
      type="error"
    >
      {{ error }}
    </FieldInfo>

    <slot
      name="info"
      :input-id="inputId"
    />
  </div>
</template>

<style lang="scss" module="css">
.field {
  display: flex;
  flex-direction: column;
  transition:
    border-color 0.15s ease-out,
    background-color 0.15s ease-out,
    color 0.15s ease-out;

  --label-color: #{$color-text};
  --placeholder-color: #{$color-gray-600};
  --input-color: #{$color-text};
  --input-border-color: #{$color-gray-600};
  --input-border-width: 1px;
  --input-background-color: #{$color-white};

  &.isValue {
    --label-color: #{$color-text};

    input::-webkit-datetime-edit {
      display: block;
    }
  }

  &.isError {
    --label-color: #{$color-patient-600};
    --input-background-color: #{$color-white};
    --input-border-color: #{$color-patient-600};
  }

  &.isFocused {
    input::-webkit-datetime-edit {
      display: block;
    }
  }

  &.isReadonly {
    --input-border-color: #{$color-gray-300};
    --input-background-color: #{$color-white};
    --label-color: #{$color-text};
  }

  &.isDisabled {
    --input-border-color: #{$color-gray-500};
    --input-background-color: #{$color-gray-50};
    --label-color: #{$color-gray-600};
    --input-color: #{$color-gray-600};
  }
}

.header {
  display: flex;
  align-items: center;
  gap: $spacing-04;
  font-size: $font-size-16;
  font-weight: $font-weight-500;
  color: var(--label-color);
}

.label {
  margin-right: auto;
  margin-block: 0.25em;
  font-size: $font-size-14;
}

.required {
  color: $color-patient-600;
  font-weight: $font-weight-700;
}

.optional {
  color: $color-gray-600;
  font-size: $font-size-12;
  font-weight: $font-weight-400;
}

.input {
  display: flex;
  position: relative;
  font-size: $font-size-16;
  line-height: 135%;
  border-radius: $border-radius-08;
  border: var(--input-border-width) solid var(--input-border-color);
  background-color: var(--input-background-color);
  overflow: hidden;
  color: var(--input-color);

  .isFocused & {
    @include input-focus-outline;
  }

  input,
  select,
  textarea {
    color: var(--input-color);
    width: 100%;
    border: none;
    background: none;
    resize: none;
    overflow: hidden;
    outline: none;

    &::placeholder {
      color: var(--placeholder-color);
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &::-webkit-calendar-picker-indicator {
      display: none;
    }

    &::-webkit-datetime-edit {
      display: none;
    }
  }

  input,
  select {
    padding: 0 $spacing-16;
    height: $input-height;
    line-height: $input-height;
  }

  textarea {
    padding: $spacing-12 $spacing-16;
    line-height: 1.5;
  }
}

.addon {
  position: absolute;
  top: 0;
  right: 0;
  height: $input-height;
  display: flex;
  align-items: center;
  padding-right: $spacing-12;
}
</style>
